import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/toast.utils";
import { captureException, sendCountMetric, SentryMetric } from "@/utils/sentry";
import { useForgotPasswordMutation } from "@/stores/queries/auth.query";

export const useForgotPasswordForm = (initialEmail?: string) => {
  const { t } = useTranslation();
  
  // Use the mutation hook
  const { mutate: forgotPassword, isPending, isSuccess } = useForgotPasswordMutation();

  // Define forgot password form schema
  const forgotPasswordSchema = z.object({
    email: z
      .string()
      .min(1, t("errors.required"))
      .email({ message: t("errors.invalidEmail") }),
  });

  type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

  // Initialize the form with zod resolver
  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: initialEmail || "",
    },
    mode: "onBlur",
  });

  const handleSubmit = (values: ForgotPasswordFormValues) => {
    forgotPassword(
      { email: values.email },
      {
        onSuccess: () => {
          toast.success(t("auth.passwordResetEmailSent"));
          sendCountMetric(SentryMetric.FORGOT_PASSWORD_SUCCESS);
        },
        onError: (error) => {
          sendCountMetric(SentryMetric.FORGOT_PASSWORD_FAILURE);
          console.error("Forgot password error:", error);
          captureException(error as Error, {
            extra: {
              component: 'useForgotPasswordForm',
              action: 'handleSubmit',
            },
          });
          toast.error(t("auth.passwordResetEmailError"));
        },
      }
    );
  };

  return {
    form,
    handleSubmit,
    isLoading: isPending,
    isSuccess,
  };
};
